import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";

import { Icon } from "../Gamification/components/shared/icons";
import styles from "./styles";
import { OnboardingReviewCard } from "./useOnboardingReviewCard";

interface Props {
  card: OnboardingReviewCard;
}

const RequestOnboardingReview = ({ card }: Props) => {
  const {
    visible,
    state,
    fetchLoading,
    submitLoading,
    showPurchaseDialog,
    setShowPurchaseDialog,
    submitReview,
    dismissCard,
  } = card;

  if (!visible) return null;

  const renderBadge = () => {
    if (state === "reviewed") {
      return (
        <Box component="span" sx={styles.badge("reviewed")}>
          <Icon.Check size={13} /> Review complete
        </Box>
      );
    }
    if (state === "submitted") {
      return (
        <Box component="span" sx={styles.badge("submitted")}>
          <Icon.Hourglass size={13} /> Review in progress
        </Box>
      );
    }
    return (
      <Box component="span" sx={styles.badge("available")}>
        <Icon.Diamond size={13} /> Account Review
      </Box>
    );
  };

  const renderBody = () => {
    if (state === "reviewed") {
      return (
        <Typography sx={styles.body}>
          Your personalized feedback is ready. <strong>Check your inbox</strong>{" "}
          for notes on your templates, sequences, and profile.
        </Typography>
      );
    }
    if (state === "submitted") {
      return (
        <Typography sx={styles.body}>
          Our Customer Success team is reviewing your templates, follow-up
          sequences, and profile. You'll hear back from us soon.
        </Typography>
      );
    }
    return (
      <Typography sx={styles.body}>
        Have our Customer Success team review your templates, follow-up
        sequences, and profile so you're set up for success — and{" "}
        <strong>boost your reply rate</strong> before you start pitching.
      </Typography>
    );
  };

  return (
    <>
      <Dialog
        open={showPurchaseDialog}
        onClose={() => setShowPurchaseDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Your account review is ready to request 💎</DialogTitle>
        <DialogContent>
          <Typography>
            Thanks for your purchase! When you're ready, click{" "}
            <strong>Submit For Review</strong> on your home page and our
            Customer Success team will review your templates, follow-up
            sequences, and profile.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={() => setShowPurchaseDialog(false)}
            sx={{ color: "white", backgroundColor: "primary.main" }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={styles.card(state)}>
        {renderBadge()}

        <Typography sx={styles.title} variant="h1">
          {state === "reviewed"
            ? "We've reviewed your account"
            : state === "submitted"
              ? "Your account is being reviewed"
              : "Get a 1:1 account review"}
        </Typography>

        {renderBody()}

        {state === "available" && (
          <Button
            fullWidth
            disabled={submitLoading || fetchLoading}
            onClick={submitReview}
            sx={styles.cta}
          >
            {submitLoading ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : (
              <>
                Submit for review <Icon.ArrowRight size={14} />
              </>
            )}
          </Button>
        )}

        {state === "submitted" && (
          <Box sx={styles.status}>
            <Box component="span" sx={styles.spinner} />
            Typically reviewed within 3 business days
          </Box>
        )}

        {state === "reviewed" && (
          <Button disableRipple onClick={dismissCard} sx={styles.dismiss}>
            Dismiss
          </Button>
        )}
      </Box>
    </>
  );
};

export default RequestOnboardingReview;
